import { useState } from "react";
import { Check, Ban, Unlock, CalendarClock, KeyRound, LogIn } from "lucide-react";

export const storeState = (s) => {
  if (s.status === "pending") return "pending";
  if (s.status === "blocked") return "blocked";
  if (s.block_reason) return "expired";
  return "active";
};

const BADGE = {
  active: "bg-emerald-500/15 text-emerald-300",
  pending: "bg-sky-500/15 text-sky-300",
  blocked: "bg-red-500/15 text-red-300",
  expired: "bg-orange-500/15 text-orange-300",
};
const PAGE = 25;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

const Act = ({ icon: Icon, label, onClick, testId, tone = "hover:text-white" }) => (
  <button onClick={onClick} title={label} data-testid={testId}
    className={`h-7 px-2 rounded-md border border-slate-800 hover:border-slate-600 text-[11px] text-slate-300 flex items-center gap-1 transition-colors ${tone}`}>
    <Icon size={12} /> {label}
  </button>
);

export default function StoresTable({ stores, loading, onApprove, onBlock, onUnblock, onPlan, onPassword, onOpen }) {
  const [page, setPage] = useState(0);
  if (loading) return <p className="p-10 text-center text-sm text-slate-500" data-testid="stores-loading">Loading stores…</p>;
  if (!stores.length) return <p className="p-10 text-center text-sm text-slate-500" data-testid="stores-empty">No stores match.</p>;
  const pages = Math.ceil(stores.length / PAGE);
  const p = Math.min(page, pages - 1);
  const rows = stores.slice(p * PAGE, p * PAGE + PAGE);

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="stores-table">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <th className="px-5 py-3">Store</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Designs</th>
              <th className="px-4 py-3">Cards / limit</th><th className="px-4 py-3">Plan expiry</th><th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s, idx) => {
              const i = p * PAGE + idx;
              const st = storeState(s);
              const full = s.card_limit && s.cards >= s.card_limit;
              return (
                <tr key={s.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`store-row-${i}`}>
                  <td className="px-5 py-3">
                    <p className="font-semibold text-slate-100">{s.store_name || s.name || "—"}{s.city && <span className="text-slate-500 font-normal"> · {s.city}</span>}</p>
                    <p className="font-mono text-[11px] text-slate-500">{s.email}</p>
                    {s.requested_plan_name && <span className="inline-block mt-1 mr-1 px-1.5 py-px rounded bg-sky-500/15 text-sky-300 text-[10px] font-mono uppercase tracking-wider" data-testid={`store-requested-plan-${i}`}>Requested: {s.requested_plan_name}</span>}
                    {s.plan_name && <span className="inline-block mt-1 px-1.5 py-px rounded bg-amber-500/10 text-amber-300 text-[10px] font-mono uppercase tracking-wider" data-testid={`store-plan-name-${i}`}>{s.plan_name}</span>}
                  </td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${BADGE[st]}`} data-testid={`store-status-${i}`}>{st}</span></td>
                  <td className="px-4 py-3 font-mono text-slate-300">{s.designs}</td>
                  <td className="px-4 py-3 font-mono" data-testid={`store-usage-${i}`}>
                    <span className={full ? "text-red-300" : "text-slate-300"}>{s.cards}</span>
                    <span className="text-slate-500"> / {s.card_limit || "∞"}</span>
                  </td>
                  <td className={`px-4 py-3 ${st === "expired" ? "text-orange-300" : "text-slate-400"}`} data-testid={`store-expiry-${i}`}>{s.expires_at ? fmt(s.expires_at) : "No expiry"}</td>
                  <td className="px-4 py-3 text-slate-500">{fmt(s.last_login)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {s.status === "pending" && <Act icon={Check} label={s.requested_plan_name ? `Approve + ${s.requested_plan_name}` : "Approve"} onClick={() => onApprove(s)} testId={`store-approve-${i}`} tone="text-emerald-300 hover:text-emerald-200" />}
                      {s.status === "blocked"
                        ? <Act icon={Unlock} label="Unblock" onClick={() => onUnblock(s)} testId={`store-unblock-${i}`} />
                        : <Act icon={Ban} label="Block" onClick={() => onBlock(s)} testId={`store-block-${i}`} tone="hover:text-red-300" />}
                      <Act icon={CalendarClock} label="Plan" onClick={() => onPlan(s)} testId={`store-plan-${i}`} />
                      <Act icon={KeyRound} label="Password" onClick={() => onPassword(s)} testId={`store-password-${i}`} />
                      <Act icon={LogIn} label="Open" onClick={() => onOpen(s)} testId={`store-open-${i}`} tone="text-amber-300 hover:text-amber-200" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 text-xs text-slate-500" data-testid="stores-pagination">
          <span>{p * PAGE + 1}–{Math.min(stores.length, p * PAGE + PAGE)} of {stores.length}</span>
          <div className="flex gap-1">
            <button className="cf-btn-ghost h-7 text-[11px]" disabled={p === 0} onClick={() => setPage(p - 1)} data-testid="stores-prev">Prev</button>
            <button className="cf-btn-ghost h-7 text-[11px]" disabled={p >= pages - 1} onClick={() => setPage(p + 1)} data-testid="stores-next">Next</button>
          </div>
        </div>
      )}
    </>
  );
}
